import { useRef, useState } from "react";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import {
  RequestLWAStartAuthRequest,
  RequestUpdatePersonalUserInfoRequest,
} from "../../services/OpenAPI";
import { useApiUpdatePersonalUserInfo } from "../api/useApiUpdatePersonalUserInfo";
import { loginUserAtom } from "../../recoil/atoms/loginUserAtoms";
import { Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import LWA from "../../assets/LWA.png";
import LWA_Pushed from "../../assets/LWA_pushed.png";
import { useApiStartLwaAuth } from "../api/useApiStartLwaAuth";

export type SearchGenreOption = {
  id: string;
  label: string;
};

export const useUserInfo = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { updatePersonalUserInfo } = useApiUpdatePersonalUserInfo();
  const loginUser = useRecoilValue(loginUserSelector);
  const { startLwaAuth } = useApiStartLwaAuth();

  const setLoginUser = useSetRecoilState(loginUserAtom);

  const [lastName, setLastName] = useState(loginUser?.lastName ?? "");
  const [firstName, setFirstName] = useState(loginUser?.firstName ?? "");
  const [mailAddress, setMailAddress] = useState(loginUser?.mail ?? "");
  const [driverName, setDriverName] = useState(loginUser?.driverName ?? "");
  const [shopName, setShopName] = useState(loginUser?.shopName ?? "");
  const [sellerId, setSellerId] = useState("");
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSellerId, setShowSellerId] = useState(false);

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [changeSellerIdDialogOpen, setChangeSellerIdDialogOpen] =
    useState(false);
  const [amazonSettingDialogOpen, setAmazonSettingDialogOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const isPasswordValid = newPassword.length >= 8;
  const isValidLength = newPassword.length >= 8;
  const isPasswordMatch = newPassword === confirmPassword;

  const handleCloseToast = () => setToastOpen(false);

  const validationCheck = () => {
    return (
      ((oldPassword.trim() === "" &&
        newPassword.trim() === "" &&
        confirmPassword.trim() === "") ||
        (oldPassword.length > 0 &&
          newPassword.length >= 8 &&
          confirmPassword === newPassword)) &&
      lastName.trim().length > 0 &&
      firstName.trim().length > 0 &&
      driverName.trim().length > 0 &&
      shopName.trim().length > 0
    );
  };

  const handleUpdateCheck = () => {
    if (sellerId.trim() === "") {
      handleUpdate();
    } else {
      setChangeSellerIdDialogOpen(true);
    }
  };

  const maskSellerId = (id: string): string => {
    if (!id) return "";
    if (id.length <= 4) return "*".repeat(id.length);
    const visible = id.slice(-4);
    const masked = "*".repeat(id.length - 4);
    return masked + visible;
  };

  const handleUpdate = async () => {
    if (
      !loginUser ||
      !loginUser.tenantId ||
      !loginUser.id ||
      !loginUser.permissionId
    )
      return;

    const request: RequestUpdatePersonalUserInfoRequest = {
      id: Number(loginUser.id),
      tenantId: loginUser.tenantId,
      lastName: lastName,
      firstName: firstName,
      mail: mailAddress,
      shopName: shopName,
      driverName: driverName,
      isChangePassword: isResetPassword,
      oldPassword: oldPassword,
      newPassword: newPassword,
      sellerId: sellerId !== "" ? sellerId : undefined,
    };
    const result = await updatePersonalUserInfo(request);
    if (result) {
      setToastMessage("更新が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      if (sellerId !== "") {
        setLoginUser((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            sellerId: sellerId !== "" ? maskSellerId(sellerId) : undefined,
          },
        }));

        // localStorage
        const stored = localStorage.getItem("user");
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.sellerId =
            sellerId !== "" ? maskSellerId(sellerId) : undefined;
          localStorage.setItem("user", JSON.stringify(parsed));
        }
      }
    } else {
      setToastMessage("更新に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const handleUpdateAmazonInfo = async () => {
    // const req: RequestUpdateAmazonInfoRequest = {
    //   tenantId: loginUser?.tenantId??0,
    //   userId: loginUser?.id ??0
    //   sellerId: "",
    //   mwsToken: "",
    // };
  };

  const ImageButton = styled(Button)({
    backgroundImage: `url(${LWA})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "contain",
    height: "50px",
    "&:hover": {
      backgroundImage: `url(${LWA_Pushed})`,
    },
    "&:active": {
      backgroundImage: `url(${LWA_Pushed})`,
    },
    color: "transparent",
  });

  const getEnv = () => {
    const hostname = window.location.hostname;
    if (hostname === "fba.rakulogi.com") {
      return "prod";
    } else {
      return "staging";
    }
  };

  const handleConnectAmazon = async () => {
    const req: RequestLWAStartAuthRequest = {
      tenantId: loginUser?.tenantId ?? 0,
      userId: loginUser?.id ?? 0,
      env: getEnv(),
    };
    const res = await startLwaAuth(req);

    if (res?.authorize_url) {
      // window.open(res.authorize_url, "_blank", "noopener,noreferrer"); // ← ここでAmazonの承認画面へ飛ばす
      window.location.href = res.authorize_url; // ← ここでAmazonの承認画面へ飛ばす
    } else {
      // エラーハンドリング
    }
  };

  return {
    inputRef,
    handleUpdateCheck,
    handleUpdate,
    detailDialogOpen,
    setDetailDialogOpen,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    lastName,
    setLastName,
    firstName,
    setFirstName,
    mailAddress,
    setMailAddress,
    driverName,
    setDriverName,
    shopName,
    setShopName,
    isResetPassword,
    setIsResetPassword,
    oldPassword,
    setOldPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    showOldPassword,
    setShowOldPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    isPasswordValid,
    isValidLength,
    isPasswordMatch,
    validationCheck,
    showSellerId,
    setShowSellerId,
    sellerId,
    setSellerId,
    changeSellerIdDialogOpen,
    setChangeSellerIdDialogOpen,
    amazonSettingDialogOpen,
    setAmazonSettingDialogOpen,
    handleUpdateAmazonInfo,
    ImageButton,
    handleConnectAmazon,
  };
};
